export const TOP_LEVEL_MODULES = [
  'dashboard',
  'cms',
  'reporting',
  'extensions',
  'trunks',
  'ivrs',
  'routes',
  'phones',
  'chat',
  'auditLog',
] as const;

export const DASHBOARD_WIDGETS = [
  'quickActions',
  'liveWidgets',
  'todayStats',
  'analytics',
  'extensionStatus',
  'queueMonitor',
  'activeCalls',
  'inboundRouting',
  'recentCalls',
] as const;

export type TopLevelModule = (typeof TOP_LEVEL_MODULES)[number];
export type DashboardWidget = (typeof DASHBOARD_WIDGETS)[number];
export type ModuleLevel = 'off' | 'view' | 'manage';
export type StoredModuleValue = boolean | ModuleLevel | string;

export type ModuleAccess = {
  modules: Record<TopLevelModule, ModuleLevel>;
  dashboardWidgets: Record<DashboardWidget, boolean>;
};

export const MODULE_DEFINITIONS: Array<{ id: TopLevelModule; label: string }> = [
  { id: 'dashboard', label: 'Overview' },
  { id: 'extensions', label: 'Extensions' },
  { id: 'trunks', label: 'Trunks' },
  { id: 'ivrs', label: 'IVRs' },
  { id: 'routes', label: 'Routes' },
  { id: 'phones', label: 'Phones' },
  { id: 'reporting', label: 'Reports' },
  { id: 'chat', label: 'Chat' },
  { id: 'auditLog', label: 'Audit log' },
  { id: 'cms', label: 'CRM (contacts)' },
];

export const DASHBOARD_WIDGET_DEFINITIONS: Array<{ id: DashboardWidget; label: string }> =
  [
    { id: 'quickActions', label: 'Quick Actions' },
    { id: 'liveWidgets', label: 'Live Widgets' },
    { id: 'todayStats', label: 'Today Stats' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'extensionStatus', label: 'Extension Status' },
    { id: 'queueMonitor', label: 'Queue Monitor' },
    { id: 'activeCalls', label: 'Active Calls' },
    { id: 'inboundRouting', label: 'Inbound Routing' },
    { id: 'recentCalls', label: 'Recent Calls' },
  ];

export const MODULE_LEVELS: ModuleLevel[] = ['off', 'view', 'manage'];

export const ENTITLEMENT_PRESETS = {
  readonly: {
    label: 'Read-only customer',
    modules: {
      dashboard: 'view',
      reporting: 'view',
      extensions: 'view',
      trunks: 'view',
      ivrs: 'view',
      routes: 'view',
      phones: 'view',
      cms: 'view',
      chat: 'view',
      auditLog: 'view',
    } satisfies Partial<Record<TopLevelModule, ModuleLevel>>,
  },
  selfService: {
    label: 'Self-service customer',
    modules: {
      dashboard: 'view',
      reporting: 'view',
      cms: 'manage',
      chat: 'view',
      extensions: 'off',
      trunks: 'off',
      ivrs: 'off',
      routes: 'view',
      phones: 'off',
      auditLog: 'off',
    } satisfies Partial<Record<TopLevelModule, ModuleLevel>>,
  },
  full: {
    label: 'Full module access',
    modules: Object.fromEntries(TOP_LEVEL_MODULES.map((id) => [id, 'manage'])) as Record<
      TopLevelModule,
      ModuleLevel
    >,
  },
} as const;

export function coerceModuleLevel(
  value: StoredModuleValue | undefined,
  fallback: ModuleLevel
): ModuleLevel {
  if (value === true) return 'manage';
  if (value === false) return 'off';
  if (value === 'off' || value === 'view' || value === 'manage') return value;
  return fallback;
}

export function moduleLevelAtLeast(level: ModuleLevel, required: 'view' | 'manage') {
  if (level === 'off') return false;
  if (required === 'view') return level === 'view' || level === 'manage';
  return level === 'manage';
}

export function defaultModuleAccess(): ModuleAccess {
  return {
    modules: Object.fromEntries(TOP_LEVEL_MODULES.map((id) => [id, 'manage'])) as Record<
      TopLevelModule,
      ModuleLevel
    >,
    dashboardWidgets: Object.fromEntries(
      DASHBOARD_WIDGETS.map((id) => [id, true])
    ) as Record<DashboardWidget, boolean>,
  };
}

export function normalizeModuleAccess(
  input?: {
    modules?: Partial<Record<TopLevelModule, StoredModuleValue>>;
    dashboardWidgets?: Partial<Record<DashboardWidget, boolean>>;
  } | null
): ModuleAccess {
  return {
    modules: Object.fromEntries(
      TOP_LEVEL_MODULES.map((id) => [id, coerceModuleLevel(input?.modules?.[id], 'off')])
    ) as Record<TopLevelModule, ModuleLevel>,
    dashboardWidgets: Object.fromEntries(
      DASHBOARD_WIDGETS.map((id) => [id, input?.dashboardWidgets?.[id] ?? false])
    ) as Record<DashboardWidget, boolean>,
  };
}

/**
 * Restrict a requested access profile to an upper-bound profile.
 *
 * Customer access is the entitlement ceiling and user access is the requested
 * subset. Keeping this in one shared helper ensures the UI and every backend
 * authorization path resolve the two layers in exactly the same way.
 */
export function intersectModuleAccess(
  upperBoundInput: {
    modules?: Partial<Record<TopLevelModule, StoredModuleValue>>;
    dashboardWidgets?: Partial<Record<DashboardWidget, boolean>>;
  },
  requestedInput: {
    modules?: Partial<Record<TopLevelModule, StoredModuleValue>>;
    dashboardWidgets?: Partial<Record<DashboardWidget, boolean>>;
  }
): ModuleAccess {
  const upperBound = normalizeModuleAccess(upperBoundInput);
  const requested = normalizeModuleAccess(requestedInput);
  const levels: ModuleLevel[] = ['off', 'view', 'manage'];

  return {
    modules: Object.fromEntries(
      TOP_LEVEL_MODULES.map((id) => {
        const upperBoundRank = levels.indexOf(upperBound.modules[id]);
        const requestedRank = levels.indexOf(requested.modules[id]);
        return [id, levels[Math.min(upperBoundRank, requestedRank)]];
      })
    ) as Record<TopLevelModule, ModuleLevel>,
    dashboardWidgets: Object.fromEntries(
      DASHBOARD_WIDGETS.map((id) => [
        id,
        upperBound.dashboardWidgets[id] && requested.dashboardWidgets[id],
      ])
    ) as Record<DashboardWidget, boolean>,
  };
}

export function hasEnabledTopLevelModule(access: ModuleAccess) {
  return TOP_LEVEL_MODULES.some((id) => access.modules[id] !== 'off');
}

export function canManageModule(access: ModuleAccess, moduleId: TopLevelModule) {
  return moduleLevelAtLeast(access.modules[moduleId], 'manage');
}

export function canViewModule(access: ModuleAccess, moduleId: TopLevelModule) {
  return moduleLevelAtLeast(access.modules[moduleId], 'view');
}
